import { Fragment, useEffect, useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Plus,
  X,
} from 'lucide-react';
import axiosInstance from '../../../../axiosConfig';

const REPORT_API = '/internal-api/v1/client-web/bookings-panel/order-reports';
const emptyFilters = () => ({
  search: '',
  fromDate: '',
  toDate: '',
  country: '',
  merchant: '',
  bookingStatus: '',
  paymentStatus: '',
  userSource: '',
  terminal: '',
  currency: '',
  ticketRules: [],
});
const inputClass =
  'w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue';
const buttonClass =
  'px-4 py-2 text-sm font-semibold border rounded-lg text-slate-600 border-slate-300 hover:bg-slate-50 disabled:opacity-50';
const money = (value) =>
  Number(value ?? 0).toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const paymentLabels = {
  PAYMENT_DONE: 'Paid',
  PENDING_PAYMENT: 'Pending',
  PAYMENT_FAILED: 'Failed',
};

export function reportParams(filters, start, length) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (key !== 'ticketRules' && value !== '') params.set(key, value);
  });
  filters.ticketRules.forEach((rule, index) => {
    params.set(`ticketRules[${index}][ticketId]`, rule.ticketId);
    if (rule.priceLkr !== '') params.set(`ticketRules[${index}][priceLkr]`, rule.priceLkr);
  });
  if (start !== undefined) params.set('start', start);
  if (length !== undefined) params.set('length', length);
  return params;
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  options = [],
  placeholder = 'All',
  disabled = false,
}) {
  return (
    <div>
      <label htmlFor={id} className="block mb-1.5 text-xs font-semibold text-slate-700">
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          className={`${inputClass} pr-8 appearance-none disabled:bg-slate-50`}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-slate-400" />
      </div>
    </div>
  );
}

function errorMessage(error, fallback) {
  if (error.response?.status === 403)
    return 'You do not have permission to view or export order reports.';
  const errors = error.response?.data?.formErrors?.errors;
  if (errors) return Object.values(errors).flat().join(' ');
  return error.response?.data?.error?.message || fallback;
}

export default function OrderListing() {
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [preset, setPreset] = useState('');
  const [metaData, setMetaData] = useState({});
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState('');
  const [orders, setOrders] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    axiosInstance
      .get(`${REPORT_API}/filter-meta`, { signal: controller.signal })
      .then(({ data }) => {
        if (!controller.signal.aborted) setMetaData(data.result || {});
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setMetaError(errorMessage(err, 'Unable to load report filter options.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setMetaLoading(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setExpandedOrder(null);
    const params = reportParams(appliedFilters, (currentPage - 1) * itemsPerPage, itemsPerPage);
    axiosInstance
      .get(`${REPORT_API}/list?${params}`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setOrders(data.data || []);
        setTotalRecords(Number(data.recordsFiltered ?? 0));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(err, 'Failed to fetch orders. Please try again later.'));
        setOrders([]);
        setTotalRecords(0);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [appliedFilters, currentPage, itemsPerPage]);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
      ...(key === 'userSource' && value === 'PLATFORM' ? { terminal: '' } : {}),
    }));
    setPreset('');
  };

  const applyPreset = (id) => {
    setPreset(id);
    const selected = metaData.presets?.find((item) => item.id === id);
    setFilters((previous) => ({
      ...previous,
      paymentStatus: selected?.paymentStatus || '',
      ticketRules: selected?.ticketRules.map((rule) => ({ ...rule })) || [],
    }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
      setError('Start date cannot be after end date.');
      return;
    }
    if (
      filters.ticketRules.some(
        (rule) =>
          !rule.ticketId ||
          (rule.priceLkr !== '' && !/^[0-9]{1,12}(\.[0-9]{1,2})?$/.test(rule.priceLkr))
      )
    ) {
      setError(
        'Select a ticket for each condition and use an LKR price with up to two decimal places.'
      );
      return;
    }
    setError('');
    setCurrentPage(1);
    setAppliedFilters({
      ...filters,
      ticketRules: filters.ticketRules.map((rule) => ({ ...rule })),
    });
  };

  const clearFilters = () => {
    setFilters(emptyFilters());
    setAppliedFilters(emptyFilters());
    setPreset('');
    setCurrentPage(1);
    setError('');
  };

  const exportOrders = async () => {
    setExporting(true);
    setError('');
    try {
      const response = await axiosInstance.get(
        `${REPORT_API}/export?${reportParams(appliedFilters)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'order-report.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          err.response.data = JSON.parse(await err.response.data.text());
        } catch {
          /* Use the fallback for non-JSON errors. */
        }
      }
      setError(errorMessage(err, 'Failed to export orders. Please try again later.'));
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const pendingChanges = JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  const updateRule = (index, key, value) =>
    updateFilter(
      'ticketRules',
      filters.ticketRules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, [key]: value } : rule
      )
    );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Orders</h1>
        <p className="mt-0.5 text-xs text-slate-500">Home / Orders</p>
      </div>

      <form onSubmit={applyFilters} className="p-6 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-semibold text-slate-800">Filter Orders</h2>
        {metaError && (
          <p role="alert" className="mb-4 text-sm text-red-700">
            {metaError}
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-4">
          <div>
            <label
              htmlFor="order-search"
              className="block mb-1.5 text-xs font-semibold text-slate-700"
            >
              Search
            </label>
            <div className="relative">
              <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
              <input
                id="order-search"
                className={`${inputClass} pl-10`}
                maxLength={200}
                placeholder="Order, customer, email, phone, external ID"
                value={filters.search}
                onChange={(event) => updateFilter('search', event.target.value)}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="order-from"
              className="block mb-1.5 text-xs font-semibold text-slate-700"
            >
              Order date from
            </label>
            <input
              id="order-from"
              type="date"
              className={inputClass}
              value={filters.fromDate}
              onChange={(event) => updateFilter('fromDate', event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="order-to" className="block mb-1.5 text-xs font-semibold text-slate-700">
              Order date to
            </label>
            <input
              id="order-to"
              type="date"
              className={inputClass}
              value={filters.toDate}
              onChange={(event) => updateFilter('toDate', event.target.value)}
            />
          </div>
          <FilterSelect
            id="order-preset"
            label="Report preset"
            value={preset}
            onChange={applyPreset}
            options={metaData.presets}
            placeholder={metaLoading ? 'Loading options...' : 'Custom / all orders'}
            disabled={metaLoading}
          />
          <FilterSelect
            id="order-source"
            label="Customer source"
            value={filters.userSource}
            onChange={(value) => updateFilter('userSource', value)}
            options={metaData.userSources}
          />
          <FilterSelect
            id="order-terminal"
            label="SSO terminal"
            value={filters.terminal}
            onChange={(value) => updateFilter('terminal', value)}
            options={metaData.terminals}
            disabled={filters.userSource === 'PLATFORM'}
          />
          <FilterSelect
            id="order-country"
            label="Customer country"
            value={filters.country}
            onChange={(value) => updateFilter('country', value)}
            options={[{ id: 'UNKNOWN', name: 'Unknown' }, ...(metaData.countries || [])]}
          />
          <FilterSelect
            id="order-merchant"
            label="Ticket merchant"
            value={filters.merchant}
            onChange={(value) => updateFilter('merchant', value)}
            options={metaData.merchants}
          />
          <FilterSelect
            id="order-payment"
            label="Payment status"
            value={filters.paymentStatus}
            onChange={(value) => updateFilter('paymentStatus', value)}
            options={metaData.paymentStates}
          />
          <FilterSelect
            id="order-booking"
            label="Booking status"
            value={filters.bookingStatus}
            onChange={(value) => updateFilter('bookingStatus', value)}
            options={metaData.bookingStates}
          />
          <FilterSelect
            id="order-currency"
            label="Order currency"
            value={filters.currency}
            onChange={(value) => updateFilter('currency', value)}
            options={metaData.currencies}
          />
        </div>

        <div className="pt-4 mt-5 border-t border-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div>
              <h3 className="text-xs font-semibold text-slate-700">Ticket conditions</h3>
              <p className="mt-1 text-xs text-slate-500">
                Match any condition. Leave the price empty to include every price for that ticket.
              </p>
            </div>
            <button
              type="button"
              className={`${buttonClass} flex items-center gap-1`}
              disabled={metaLoading || filters.ticketRules.length >= 50}
              onClick={() =>
                updateFilter('ticketRules', [
                  ...filters.ticketRules,
                  { ticketId: '', priceLkr: '' },
                ])
              }
            >
              <Plus size={14} /> Add ticket
            </button>
          </div>
          {filters.ticketRules.map((rule, index) => {
            const ticketOptions = metaData.tickets || [];
            const options = ticketOptions.map((ticket) => ({
              id: ticket.id,
              name: `${ticket.id} - ${ticket.name}`,
            }));
            if (
              rule.ticketId &&
              !ticketOptions.some((ticket) => String(ticket.id) === String(rule.ticketId))
            )
              options.push({ id: rule.ticketId, name: `Ticket ${rule.ticketId}` });
            return (
              <div
                key={index}
                className="grid items-end grid-cols-1 gap-3 p-3 mt-2 rounded-lg bg-slate-50 md:grid-cols-[2fr_1fr_auto]"
              >
                <FilterSelect
                  id={`ticket-${index}`}
                  label={`Ticket ${index + 1}`}
                  value={rule.ticketId}
                  onChange={(value) => updateRule(index, 'ticketId', value)}
                  options={options}
                  placeholder="Select ticket"
                />
                <div>
                  <label
                    htmlFor={`price-${index}`}
                    className="block mb-1.5 text-xs font-semibold text-slate-700"
                  >
                    Exact unit price (LKR)
                  </label>
                  <input
                    id={`price-${index}`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Any price"
                    className={inputClass}
                    value={rule.priceLkr}
                    onChange={(event) => updateRule(index, 'priceLkr', event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  aria-label={`Remove ticket condition ${index + 1}`}
                  className={buttonClass}
                  onClick={() =>
                    updateFilter(
                      'ticketRules',
                      filters.ticketRules.filter((_, ruleIndex) => ruleIndex !== index)
                    )
                  }
                >
                  <X size={18} />
                </button>
              </div>
            );
          })}
        </div>

        {error && (
          <p role="alert" className="p-3 mt-4 text-sm text-red-700 rounded-lg bg-red-50">
            {error}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-3 mt-5">
          {pendingChanges && (
            <span className="mr-auto text-xs text-amber-700">
              Apply changes to update the list and export.
            </span>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-50"
          >
            Apply Filters
          </button>
          <button type="button" onClick={clearFilters} disabled={loading} className={buttonClass}>
            Clear Filters
          </button>
          <button
            type="button"
            onClick={exportOrders}
            disabled={loading || exporting || totalRecords === 0 || pendingChanges}
            className={`${buttonClass} flex items-center gap-1.5`}
          >
            {exporting ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </form>

      <p className="text-xs text-slate-500">
        One row per cart and booking. Ticket counts and LKR amounts include matching items only.
        Full order amounts can repeat across bookings. Dates are in Sri Lanka time.
      </p>
      <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <caption className="sr-only">Order report</caption>
            <thead className="text-xs tracking-wide text-slate-500 bg-slate-50">
              <tr>
                {[
                  'Order ID',
                  'Customer',
                  'Source',
                  'Order date',
                  'Matching tickets',
                  'Matching amount (LKR)',
                  'Ticket details',
                  'Full order amount',
                  'Payment status',
                  'Booking status',
                  'Actions',
                ].map((label) => (
                  <th key={label} scope="col" className="px-5 py-3 font-semibold whitespace-nowrap">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="py-10 text-center text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <RefreshCw size={16} className="animate-spin" />
                      Loading orders...
                    </span>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-10 text-center text-slate-500">
                    {error ? 'Order report unavailable' : 'No orders found'}
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const key = `${order.cartId}-${order.bookingId}`;
                  const expanded = expandedOrder === key;
                  return (
                    <Fragment key={key}>
                      <tr className="hover:bg-slate-50">
                        <td className="px-5 py-4 font-semibold whitespace-nowrap text-brand-blue">
                          {order.orderId || '—'}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="font-medium text-slate-800">
                            {order.customerName || 'Unknown customer'}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">{order.email || '—'}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {order.contactNumber || '—'}
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-brand-blue/10 text-brand-blue">
                            {order.userSource === 'SSO' ? 'SSO' : 'Platform'}
                          </span>
                          {order.terminalNames && (
                            <div className="mt-2 text-xs text-slate-500">{order.terminalNames}</div>
                          )}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-slate-700">
                          {order.orderDateTime || '—'}
                        </td>
                        <td className="px-5 py-4 text-right">
                          {Number(order.numberOfTickets ?? 0).toLocaleString()}
                        </td>
                        <td className="px-5 py-4 font-semibold text-right whitespace-nowrap">
                          {money(order.totalAmountLkr)}
                        </td>
                        <td className="min-w-[220px] px-5 py-4 text-slate-700">
                          {order.ticketDetails || '—'}
                        </td>
                        <td className="px-5 py-4 font-semibold whitespace-nowrap">
                          {order.orderCurrency || '—'} {money(order.fullOrderAmount)}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${order.paymentStatus === 'PAYMENT_DONE' ? 'bg-emerald-50 text-emerald-700' : order.paymentStatus === 'PAYMENT_FAILED' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}
                          >
                            {paymentLabels[order.paymentStatus] || order.paymentStatus}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-slate-700">
                          {order.bookingStatusName || order.bookingStatus || '—'}
                        </td>
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            className={buttonClass}
                            aria-expanded={expanded}
                            aria-controls={`details-${key}`}
                            onClick={() => setExpandedOrder(expanded ? null : key)}
                          >
                            {expanded ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr id={`details-${key}`}>
                          <td colSpan={11} className="px-5 py-4 bg-slate-50">
                            <dl className="grid gap-4 text-xs md:grid-cols-4">
                              {[
                                ['Booking ID', order.bookingUuid],
                                ['Customer source', order.userSource],
                                ['SSO terminal', order.terminalNames],
                                ['External user ID', order.externalUserIds],
                              ].map(([label, value]) => (
                                <div key={label}>
                                  <dt className="font-semibold text-slate-500">{label}</dt>
                                  <dd className="mt-1 break-all text-slate-800">{value || '—'}</dd>
                                </div>
                              ))}
                            </dl>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 border-t border-slate-200">
          <span className="text-xs text-slate-500" role="status">
            Showing {totalRecords ? (currentPage - 1) * itemsPerPage + 1 : 0}–
            {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} rows
          </span>
          <div className="flex items-center gap-3">
            <label htmlFor="order-page-size" className="text-xs text-slate-500">
              Rows per page
            </label>
            <select
              id="order-page-size"
              className="p-1 text-xs bg-white border rounded-lg border-slate-300"
              value={itemsPerPage}
              disabled={loading}
              onChange={(event) => {
                setItemsPerPage(Number(event.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <nav aria-label="Order pagination" className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Previous page"
                disabled={loading || currentPage === 1}
                className="p-1 border rounded-lg disabled:opacity-50"
                onClick={() => setCurrentPage((page) => page - 1)}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-xs text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={loading || currentPage >= totalPages}
                className="p-1 border rounded-lg disabled:opacity-50"
                onClick={() => setCurrentPage((page) => page + 1)}
              >
                <ChevronRight size={18} />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
